import { actions as actions$1, idleState, loadingState, failedState, useSelector, useIntl, statusLoading, useDispatch, useSaga, ReducersRegistry } from '@grandvisionhq/state';
import * as swcHelpers from '@swc/helpers';
import { createSelector } from 'reselect';
import { isDefined } from '@grandvisionhq/utils';
import * as React from 'react';
import React__default from 'react';
import { all, takeLatest, call, select, put, getContext, cancel } from 'redux-saga/effects';
import classnames from 'classnames';
import { Modal, Button, MediaQuery, IconButton, WizardProgress } from '@grandvisionhq/common';
import { Heading, Text } from '@grandvisionhq/elements';
import { motion } from 'framer-motion';
import { Icon } from '@grandvisionhq/icons';
import { Form } from '@grandvisionhq/forms';

const idPayload = actions$1.payload();
const startConfigurator = actions$1.action('configurator/start', actions$1.payload());
const isStartConfiguratorWithId = (id)=>(a)=>actions$1.isType(a, startConfigurator) && a.payload.id === id
;
/**
 * Stepping actions
 */ const nextStep = actions$1.action('configurator/next-step', idPayload);
const isNextStepWithId = (id)=>(a)=>actions$1.isType(a, nextStep) && a.payload.id === id
;
const nextStepSuccess = actions$1.action('configurator/next-step-success', actions$1.payload());
const isNextStepSuccessWithId = (id)=>(a)=>actions$1.isType(a, nextStepSuccess) && a.payload.id === id
;
const nextStepFailed = actions$1.action('configurator/next-step-failed', actions$1.payload());
const isNextStepFailedWithId = (id)=>(a)=>actions$1.isType(a, nextStepFailed) && a.payload.id === id
;
const previousStep = actions$1.action('configurator/previous-step', idPayload);
const isPreviousStepWithId = (id)=>(a)=>actions$1.isType(a, previousStep) && a.payload.id === id
;
const previousStepFailed = actions$1.action('configurator/previous-step-failed', actions$1.payload());
const isPreviousStepFailedWithId = (id)=>(a)=>actions$1.isType(a, previousStepFailed) && a.payload.id === id
;
const previousStepSuccess = actions$1.action('configurator/previous-step-success', actions$1.payload());
const isPreviousStepSuccessWithId = (id)=>(a)=>actions$1.isType(a, previousStepSuccess) && a.payload.id === id
;
/**
 * Footer actions
 */ const toggleFooter = actions$1.action('configurator/footer/toggle', idPayload);
const isToggleFooterWithId = (id)=>(a)=>actions$1.isType(a, toggleFooter) && a.payload.id === id
;
const openFooter = actions$1.action('configurator/footer/open', idPayload);
const isOpenFooterWithId = (id)=>(a)=>actions$1.isType(a, openFooter) && a.payload.id === id
;
const closeFooter$1 = actions$1.action('configurator/footer/close', idPayload);
const isCloseFooterWithId = (id)=>(a)=>actions$1.isType(a, closeFooter$1) && a.payload.id === id
;
const stepShown = actions$1.action('configurator/step-shown', actions$1.payload());
const isStepShownWithId = (id)=>(a)=>actions$1.isType(a, stepShown) && a.payload.id === id
;
const finish = actions$1.action('configurator/finish', actions$1.payload());
const isFinishWithId = (id)=>(a)=>actions$1.isType(a, finish) && a.payload.id === id
;
const finishFailed = actions$1.action('configurator/finish-failed', actions$1.payload());
const finishSuccess = actions$1.action('configurator/finish-success', actions$1.payload());

var actions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  startConfigurator: startConfigurator,
  isStartConfiguratorWithId: isStartConfiguratorWithId,
  nextStep: nextStep,
  isNextStepWithId: isNextStepWithId,
  nextStepSuccess: nextStepSuccess,
  isNextStepSuccessWithId: isNextStepSuccessWithId,
  nextStepFailed: nextStepFailed,
  isNextStepFailedWithId: isNextStepFailedWithId,
  previousStep: previousStep,
  isPreviousStepWithId: isPreviousStepWithId,
  previousStepFailed: previousStepFailed,
  isPreviousStepFailedWithId: isPreviousStepFailedWithId,
  previousStepSuccess: previousStepSuccess,
  isPreviousStepSuccessWithId: isPreviousStepSuccessWithId,
  toggleFooter: toggleFooter,
  isToggleFooterWithId: isToggleFooterWithId,
  openFooter: openFooter,
  isOpenFooterWithId: isOpenFooterWithId,
  closeFooter: closeFooter$1,
  isCloseFooterWithId: isCloseFooterWithId,
  stepShown: stepShown,
  isStepShownWithId: isStepShownWithId,
  finish: finish,
  isFinishWithId: isFinishWithId,
  finishFailed: finishFailed,
  finishSuccess: finishSuccess
});

var ref$1;
const createInitialConfiguratorState = (stateOverrides)=>{
    var ref1;
    return swcHelpers.objectSpread({
        currentStep: 0,
        stepSequence: [],
        stepsAttempted: new Set(),
        stepsVisited: [
            (ref$1 = stateOverrides === null || stateOverrides === void 0 ? void 0 : (ref1 = stateOverrides.stepSequence) === null || ref1 === void 0 ? void 0 : ref1[0]) !== null && ref$1 !== void 0 ? ref$1 : ''
        ],
        calls: {
            finish: idleState()
        },
        isFooterOpen: false
    }, stateOverrides);
};
const setStateById = (state, id, cb)=>{
    const configuratorState = state[id];
    return configuratorState ? swcHelpers.objectSpread({}, state, {
        [id]: swcHelpers.objectSpread({}, configuratorState, cb(configuratorState))
    }) : state;
};
const initialState = {};
var _stepsAttempted;
const reducer = actions$1.reducer(initialState, /**
   * Set the correct productSku and always reset the configurator to the initial state
   */ actions$1.on(startConfigurator, (s, { payload: { id , stepSequence  }  })=>swcHelpers.objectSpread({}, s, {
        [id]: swcHelpers.objectSpread({}, createInitialConfiguratorState({
            stepSequence
        }))
    })
), /**
   * When user tries to move on to the next step record so we mark the form as submitted
   *
   * This will reveal validation errors
   */ actions$1.on(nextStep, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            stepsAttempted: new Set([
                ...(_stepsAttempted = c.stepsAttempted) !== null && _stepsAttempted !== void 0 ? _stepsAttempted : [],
                c.currentStep
            ])
        })
    )
), /**
   * When the steps change is allowed it's communicated by dispatching the nextStepSuccess action
   */ actions$1.on(nextStepSuccess, (s, { payload: { id , newStep  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            currentStep: newStep,
            stepsVisited: [
                ...new Set([
                    ...c.stepsVisited,
                    c.stepSequence[newStep]
                ])
            ]
        })
    )
), /**
   * When the current step is moved back it's communicated by dispatching the previousStepSuccess action
   *
   * When user returns from step 3 to step 2, both 3 (currentStep) and 2 (newStep) are removed so we don't show error messages when returning
   */ actions$1.on(previousStepSuccess, (s, { payload: { id , currentStepNumber , newStep  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            currentStep: newStep,
            stepsAttempted: new Set([
                ...c.stepsAttempted
            ].filter((step)=>step !== currentStepNumber && step !== newStep
            ))
        })
    )
), /**
   * Toggles footer
   */ actions$1.on(toggleFooter, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            isFooterOpen: !c.isFooterOpen
        })
    )
), actions$1.on(closeFooter$1, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            isFooterOpen: false
        })
    )
), actions$1.on(openFooter, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            isFooterOpen: true
        })
    )
), /**
   * Tracks the status of the finish call
   */ actions$1.on(finish, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            calls: swcHelpers.objectSpread({}, c.calls, {
                finish: loadingState()
            })
        })
    )
), actions$1.on(finishFailed, (s, { payload: { id , reason  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            calls: swcHelpers.objectSpread({}, c.calls, {
                finish: failedState(reason)
            })
        })
    )
), actions$1.on(finishSuccess, (s, { payload: { id  }  })=>setStateById(s, id, (c)=>swcHelpers.objectSpread({}, c, {
            calls: swcHelpers.objectSpread({}, c.calls, {
                finish: idleState()
            })
        })
    )
));

const isDynamicStep = (step)=>step.hasOwnProperty('stepStatus')
;
const ctaLabelSelectorProps = (_, props)=>props
;

/**
 * Selectors for prop forwarding
 */ const stateProp$1 = (s)=>s
;
const configuratorIdProp$1 = (_, { id  })=>id
;
const stepNameProp$1 = (_, { stepName  })=>stepName
;
const stepsMapProp = (_, { stepsMap  })=>stepsMap
;
const stepSequenceProp = (_, { stepSequence  })=>stepSequence
;
const getConfigurators = (state)=>state.configurators
;
const getConfigurator$1 = createSelector(getConfigurators, configuratorIdProp$1, (configurators, id)=>configurators[id]
);
/**
 * Step related selectors
 */ const getStepSequence = createSelector(getConfigurator$1, (c)=>{
    return c === null || c === void 0 ? void 0 : c.stepSequence;
});
const getCurrentStepNumber = createSelector(getConfigurator$1, (c)=>{
    return c === null || c === void 0 ? void 0 : c.currentStep;
});
const getStepsVisited = createSelector(getConfigurator$1, (c)=>{
    return c === null || c === void 0 ? void 0 : c.stepsVisited;
});
const getStepsList = createSelector(stepsMapProp, stepSequenceProp, getCurrentStepNumber, (s)=>s
, (stepsMap, stepSequence, currentStepNumber, s)=>(stepSequence || []).reduce((acc, stepName, i)=>{
        let stepStatus = 'active';
        if (!stepsMap) {
            return [];
        }
        const step = stepsMap[stepName];
        if (!step) {
            return acc;
        }
        if (isDynamicStep(step)) {
            stepStatus = step.stepStatus(s);
            if (stepStatus === 'hidden') {
                return acc;
            }
        }
        return [
            ...acc,
            {
                name: stepName,
                isCurrent: currentStepNumber === i,
                skip: stepStatus === 'skip',
                isSubStep: stepStatus === 'sub-step'
            }, 
        ];
    }, [])
);
const getActiveSteps = createSelector(getStepsList, (steps)=>(steps || []).filter((s)=>!s.skip
    )
);
const getCurrentStepIndex = createSelector(getStepsList, (steps)=>{
    const index = steps.findIndex((s)=>s.isCurrent
    );
    return index < 0 ? undefined : index;
});
const getCurrentStepName = createSelector(getStepsList, (steps)=>{
    var ref;
    return (ref = steps.find((s)=>s.isCurrent
    )) === null || ref === void 0 ? void 0 : ref.name;
});

var InternalSelectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  stateProp: stateProp$1,
  configuratorIdProp: configuratorIdProp$1,
  stepNameProp: stepNameProp$1,
  stepsMapProp: stepsMapProp,
  stepSequenceProp: stepSequenceProp,
  getConfigurators: getConfigurators,
  getConfigurator: getConfigurator$1,
  getStepSequence: getStepSequence,
  getCurrentStepNumber: getCurrentStepNumber,
  getStepsVisited: getStepsVisited,
  getStepsList: getStepsList,
  getActiveSteps: getActiveSteps,
  getCurrentStepIndex: getCurrentStepIndex,
  getCurrentStepName: getCurrentStepName
});

const { configuratorIdProp , stateProp , stepNameProp  } = InternalSelectors;
const getConfigurator = createSelector(configuratorIdProp, stateProp, (id, s)=>getConfigurator$1(s, {
        id
    })
);
/**
 * Exported selectors to be used in container components
 */ const hasAttemptedNextStep = createSelector(getConfigurator, (c)=>c ? c.stepsAttempted.has(c.currentStep) : false
);
const getCurrentStep = createSelector(getStepSequence, getCurrentStepNumber, configuratorIdProp, stateProp, (stepSequence, currentStep)=>{
    if (!isDefined(stepSequence) || !isDefined(currentStep)) {
        return;
    }
    return {
        name: stepSequence[currentStep]
    };
});
/**
 * Checks the step name against the list of visited steps in the state.
 */ const hasVisitedStep = createSelector(getStepsVisited, configuratorIdProp, stepNameProp, (stepsVisited, id, stepName)=>(stepsVisited || []).includes(stepName)
);
var ref;
const isFooterOpen = createSelector(getConfigurator, (c)=>{
    return (ref = c === null || c === void 0 ? void 0 : c.isFooterOpen) !== null && ref !== void 0 ? ref : false;
});
/**
 * Checks if we're on the last step, taking into consideration any steps that are disabled or to be skipped.
 */ const isLastStep = createSelector(getStepsList, (steps)=>{
    var ref1;
    if (!steps) {
        return false;
    }
    const lastStepIsCurrentStep = (ref1 = steps[steps.length - 1]) === null || ref1 === void 0 ? void 0 : ref1.isCurrent;
    if (!isDefined(lastStepIsCurrentStep)) {
        return false;
    }
    if (lastStepIsCurrentStep) {
        return true;
    }
    // We're not on the last step, so we have to check if all steps following the current steps should be skipped.
    const currentStep = steps.findIndex((s)=>s.isCurrent
    );
    const remainingSteps = steps.slice(currentStep + 1);
    return remainingSteps.every((s)=>s.skip
    );
});
const getFinishCall = createSelector(getConfigurator, (c)=>{
    return c === null || c === void 0 ? void 0 : c.calls.finish;
});

var selectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getConfigurator: getConfigurator,
  hasAttemptedNextStep: hasAttemptedNextStep,
  getCurrentStep: getCurrentStep,
  hasVisitedStep: hasVisitedStep,
  isFooterOpen: isFooterOpen,
  isLastStep: isLastStep,
  getFinishCall: getFinishCall
});

const ConfiguratorContext = /*#__PURE__*/ React.createContext(null);
const useConfiguratorContext = ()=>{
    const context = React.useContext(ConfiguratorContext);
    if (context === null) {
        throw Error(`Consumer of useConfiguratorContext needs to be wrapped in a ConfiguratorContext provider.`);
    }
    return context;
};
var ConfiguratorContextProvider = (_param)=> {
    var { children  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children"
    ]);
    return React.createElement(ConfiguratorContext.Provider, {
        value: rest
    }, children);
};

const PrivateSteppingContext = /*#__PURE__*/ React.createContext(null);
/**
 * This hook is not exported outside of this module. It is not meant to be used by consumers.
 */ const usePrivateSteppingContext = ()=>{
    const context = React.useContext(PrivateSteppingContext);
    if (context === null) {
        throw Error(`Consumer of useSteppingContext needs to be wrapped in a PrivateSteppingContext provider.`);
    }
    return context;
};
const PrivateSteppingContextProvider = ({ children , configuratorId , stepsMap , stepSequence , lastStepCTALabelKey , footerTopBarContent , footerExpanderContent ,  })=>{
    const id = configuratorId;
    const selectorProps = React.useMemo(()=>({
            id,
            stepsMap,
            stepSequence
        })
    , [
        id,
        stepsMap,
        stepSequence, 
    ]);
    const context = {
        selectorProps,
        configuratorId,
        stepsMap,
        lastStepCTALabelKey,
        footerExpanderContent,
        footerTopBarContent
    };
    return /*#__PURE__*/ React.createElement(PrivateSteppingContext.Provider, {
        value: context
    }, children);
};

/**
 * Utils
 */ function logUndefinedStep(configuratorId, currentStep) {
    console.warn(`Could not determine the current step of configurator with ID "${configuratorId}". Received ${currentStep}`);
}
function logUndefinedStepProperty(configuratorId, propertyName, propertyValue) {
    console.warn(`Could not determine property "${propertyName}" of the current step in configurator with ID "${configuratorId}". Received ${propertyValue}`);
}
function* getConfiguratorContext(id) {
    const configuratorSagaContext = yield getContext(id);
    if (!isDefined(configuratorSagaContext)) {
        console.warn(`Could not find the saga context of configurator with ID "${id}". Received ${configuratorSagaContext}`);
        return yield cancel();
    }
    const stepsMap = configuratorSagaContext.stepsMap;
    const stepSequence = configuratorSagaContext.stepSequence;
    if (!isDefined(stepsMap)) {
        logUndefinedStepProperty(id, 'stepsMap', stepsMap);
        return yield cancel();
    }
    if (!isDefined(stepSequence)) {
        logUndefinedStepProperty(id, 'stepSequence', stepSequence);
        return yield cancel();
    }
    return configuratorSagaContext;
}
function* onNextStep({ payload: { id  }  }) {
    const configuratorSagaContext = yield call(getConfiguratorContext, id);
    const { stepsMap , stepSequence  } = configuratorSagaContext;
    const activeSteps = yield select(getActiveSteps, {
        id,
        stepsMap,
        stepSequence
    });
    const currentStep = yield select(getCurrentStep, {
        id
    });
    if (!isDefined(currentStep)) {
        logUndefinedStep(id, currentStep);
        return;
    }
    const currentStepNumber = yield select(getCurrentStepNumber, {
        id
    });
    if (!isDefined(currentStepNumber)) {
        logUndefinedStepProperty(id, 'currentStepNumber', currentStepNumber);
        return;
    }
    const step = stepsMap === null || stepsMap === void 0 ? void 0 : stepsMap[currentStep.name];
    if (!isDefined(step)) {
        console.error(`Could not find step data for step with name "${currentStep.name}" in configurator with ID "${id}".`);
        return;
    }
    if (step.onNext) {
        const result = yield call(step.onNext);
        if (result) {
            yield put(nextStepFailed({
                id,
                currentStepNumber,
                reason: result
            }));
            return;
        }
    }
    /**
   * Get the name of the first next active step
   */ const nextActiveStepName = activeSteps.reduce((stepName, curr, index)=>{
        var ref;
        return curr.isCurrent ? (ref = activeSteps[index + 1]) === null || ref === void 0 ? void 0 : ref.name : stepName;
    }, undefined);
    /**
   * This means we're on the last step. We'll dispatch a `nextStepSuccess" action
   * with the same step index as the step we're currently on to inform consumers
   * that they can start handling their last step things.
   */ if (!nextActiveStepName) {
        yield put(finish({
            id
        }));
        return;
    }
    if (!isDefined(stepSequence)) {
        return;
    }
    /**
   * Get the index of first next active step from the *original* step sequence
   */ const nextActiveStepIndex = stepSequence.indexOf(nextActiveStepName);
    yield put(nextStepSuccess({
        id,
        currentStepNumber,
        newStep: nextActiveStepIndex
    }));
}
function* triggerOnEnterForFirstStep({ payload: { id , stepSequence  }  }) {
    var ref;
    const configuratorSagaContext = yield call(getConfiguratorContext, id);
    const { stepsMap  } = configuratorSagaContext;
    const steps = yield select(getStepsList, {
        id,
        stepsMap,
        stepSequence
    });
    const currentStepName = (ref = steps[0]) === null || ref === void 0 ? void 0 : ref.name;
    if (!currentStepName) {
        logUndefinedStepProperty(id, 'name', currentStepName);
        return;
    }
    const step = stepsMap[currentStepName];
    if (!isDefined(step)) {
        logUndefinedStepProperty(id, 'name', step);
        return;
    }
    const stepStatus = isDynamicStep(step) ? yield select(step.stepStatus) : yield select(()=>'active'
    );
    if (stepStatus !== 'active') {
        yield put(nextStep({
            id
        }));
        return;
    }
    if (step && step.onEnter) {
        yield call(step.onEnter);
    }
    yield put(stepShown({
        id,
        step: currentStepName
    }));
}
function* onSuccessfulStepSwitch({ payload: { id , newStep  }  }) {
    const configuratorSagaContext = yield call(getConfiguratorContext, id);
    const { stepsMap , stepSequence  } = configuratorSagaContext;
    if (!isDefined(stepSequence)) {
        logUndefinedStepProperty(id, 'stepSequence', stepSequence);
        return;
    }
    const stepName = stepSequence[newStep];
    const step = stepsMap === null || stepsMap === void 0 ? void 0 : stepsMap[stepName];
    if (!isDefined(step)) {
        logUndefinedStep(id, step);
        return;
    }
    if (step.onEnter) {
        yield call(step.onEnter);
    }
    yield put(stepShown({
        id,
        step: stepName
    }));
}
function* onPreviousStep({ payload: { id  }  }) {
    const configuratorSagaContext = yield call(getConfiguratorContext, id);
    const { stepsMap , stepSequence  } = configuratorSagaContext;
    const activeSteps = yield select(getActiveSteps, {
        id,
        stepsMap,
        stepSequence
    });
    const currentStepNumber = yield select(getCurrentStepNumber, {
        id
    });
    if (!isDefined(currentStepNumber)) {
        logUndefinedStepProperty(id, 'currentStepNumber', currentStepNumber);
        return;
    }
    /**
   * Get the name of the first previous active step
   */ const previousActiveStepName = activeSteps.reduce((stepName, curr, index)=>{
        var ref;
        return curr.isCurrent ? (ref = activeSteps[index - 1]) === null || ref === void 0 ? void 0 : ref.name : stepName;
    }, undefined);
    if (!previousActiveStepName) {
        yield put(previousStepFailed({
            id,
            currentStepNumber,
            reason: new Error(`Can't go back beyond the first step`)
        }));
        return;
    }
    /**
   * Get the index of first next active step from the *original* step sequence
   */ if (!isDefined(stepSequence)) {
        logUndefinedStepProperty(id, 'stepSequence', stepSequence);
        return;
    }
    const previousActiveStepIndex = stepSequence.indexOf(previousActiveStepName);
    yield put(previousStepSuccess({
        id,
        currentStepNumber,
        newStep: previousActiveStepIndex
    }));
}
function* closeFooter({ payload: { id  }  }) {
    yield put(closeFooter$1({
        id
    }));
}
function* triggerOnFinishForLastStep({ payload: { id  }  }) {
    const configuratorSagaContext = yield call(getConfiguratorContext, id);
    const onFinish = configuratorSagaContext === null || configuratorSagaContext === void 0 ? void 0 : configuratorSagaContext.onFinish;
    if (!onFinish) {
        yield put(finishSuccess({
            id
        }));
    }
    try {
        yield call(onFinish);
    } catch (error) {
        yield put(finishFailed({
            id,
            reason: error
        }));
    }
}
function configuratorFactory(configuratorNamespace) {
    return {
        rootSaga: function*() {
            yield all([
                takeLatest(isStartConfiguratorWithId(configuratorNamespace), triggerOnEnterForFirstStep),
                takeLatest(isNextStepWithId(configuratorNamespace), onNextStep),
                takeLatest(isPreviousStepWithId(configuratorNamespace), onPreviousStep),
                takeLatest([
                    isNextStepWithId(configuratorNamespace),
                    isPreviousStepWithId(configuratorNamespace), 
                ], closeFooter),
                takeLatest([
                    isNextStepSuccessWithId(configuratorNamespace),
                    isPreviousStepSuccessWithId(configuratorNamespace), 
                ], onSuccessfulStepSwitch),
                takeLatest(isFinishWithId(configuratorNamespace), triggerOnFinishForLastStep), 
            ]);
        }
    };
}

const CurrentStepContext = /*#__PURE__*/ React.createContext(null);
/**
 * This hook is not exported outside of this module. It is not meant to be used by consumers.
 */ const useCurrentStepContext = ()=>{
    const context = React.useContext(CurrentStepContext);
    if (context === null) {
        throw Error(`Consumer of useSteppingContext needs to be wrapped in a CurrentStepContext provider.`);
    }
    return context;
};
const CurrentStepContextProvider = ({ children  })=>{
    const { stepsMap , selectorProps  } = usePrivateSteppingContext();
    const currentStepName = useSelector((s)=>getCurrentStepName(s, selectorProps)
    );
    if (!isDefined(currentStepName)) {
        throw new Error(`currentStepName must be defined. However, "${currentStepName}" was received.`);
    }
    const currentStep = stepsMap[currentStepName];
    if (!isDefined(currentStep)) {
        throw new Error(`currentStep must be defined. However, "${currentStep}" was received.`);
    }
    const context = {
        currentStep,
        currentStepName
    };
    return /*#__PURE__*/ React.createElement(CurrentStepContext.Provider, {
        value: context
    }, children);
};

const ConfiguratorModal = ({ 'data-t': dataT , children , className , isOpen , preventVerticalScroll , onModalClose ,  })=>{
    const { getLabel  } = useIntl();
    const { goToPreviousStep  } = useConfiguratorContext();
    const { selectorProps  } = usePrivateSteppingContext();
    const steps = useSelector((s)=>getStepsList(s, selectorProps)
    );
    const currentStepIndex = useSelector((s)=>getCurrentStepIndex(s, selectorProps)
    );
    const isFirstStep = currentStepIndex === 0;
    const previousSteps = steps.slice(0, currentStepIndex);
    const allPreviousStepsSkipped = previousSteps.every((s)=>s.skip
    );
    const handleBackClick = React__default.useCallback(()=>{
        if (!isFirstStep) {
            goToPreviousStep();
        }
    }, [
        isFirstStep,
        goToPreviousStep
    ]);
    const onBackClick = isFirstStep || allPreviousStepsSkipped ? undefined : handleBackClick;
    const backLabel = getLabel('common.actions.previous');
    const secondaryIconOptions = onBackClick && {
        'aria-label': backLabel,
        name: 'chevron-left',
        onClick: onBackClick,
        label: backLabel
    };
    return /*#__PURE__*/ React__default.createElement(Modal, {
        "data-t": dataT,
        preventVerticalScroll: preventVerticalScroll,
        className: classnames('configurator-modal', className),
        secondaryIconOptions: secondaryIconOptions,
        onModalClose: onModalClose,
        isOpen: isOpen,
        isFullScreen: true,
        showIcon: true
    }, children);
};

const ConfiguratorStepTitleAndDescription = ({ title , description , titleClassName , descriptionClassName , textAlignment ='center' ,  })=>{
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "configurator-step-headings"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        component: "h2",
        variant: "medium",
        align: textAlignment,
        className: classnames('configurator-step-headings__title', titleClassName)
    }, title), description && /*#__PURE__*/ React__default.createElement(Text, {
        className: classnames('configurator-step-headings__description', descriptionClassName),
        variant: "body",
        align: textAlignment
    }, description));
};

const ConfiguratorStepTitleAndDescriptionContainer = ({ textAlignment , titleClassName , descriptionClassName ,  })=>{
    const { getLabel , getFormattedLabel , hasLabel  } = useIntl();
    const { currentStep  } = useCurrentStepContext();
    const { title , description  } = currentStep.labelKeys;
    const labels = React__default.useMemo(()=>({
            title: getLabel(title),
            description: description && hasLabel(description) ? getFormattedLabel(description, {
                parseLink: true,
                openLinksInNewTab: true,
                hideLabelKey: true
            }) : undefined
        })
    , [
        getLabel,
        title,
        description,
        getFormattedLabel,
        hasLabel
    ]);
    return /*#__PURE__*/ React__default.createElement(ConfiguratorStepTitleAndDescription, swcHelpers.extends({}, labels, {
        textAlignment: textAlignment,
        titleClassName: titleClassName,
        descriptionClassName: descriptionClassName
    }));
};

var ConfiguratorStep = (_param)=>{
    var { children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('configurator-step', className),
        "data-t": rest['data-t']
    }, children);
};

const ConfiguratorStepContainer = (props)=>{
    const { currentStep  } = useCurrentStepContext();
    const { view: CurrentStepComponent  } = currentStep;
    return /*#__PURE__*/ React.createElement(ConfiguratorStep, swcHelpers.extends({}, props), /*#__PURE__*/ React.createElement(CurrentStepComponent, null));
};

const animations = {
    expanded: {
        height: 'auto',
        overflow: 'auto',
        transition: {
            ease: 'easeOut',
            duration: 0.2
        }
    },
    collapsed: {
        height: '0px',
        overflow: 'hidden',
        transition: {
            ease: 'easeIn',
            duration: 0.2
        }
    }
};
const Expander = ({ isExpanded , children  })=>/*#__PURE__*/ React.createElement(motion.div, {
        initial: animations.collapsed,
        animate: isExpanded ? animations.expanded : animations.collapsed,
        "aria-hidden": !isExpanded,
        className: "configurator-footer-expander"
    }, children)
;

const ExpanderContainer = ({ children  })=>{
    const { isFooterOpen  } = useConfiguratorContext();
    return /*#__PURE__*/ React__default.createElement(Expander, {
        isExpanded: isFooterOpen
    }, children);
};

const CallToAction = ({ formId , label , isLoading , isSmallScreen  })=>/*#__PURE__*/ React.createElement(Button, {
        type: "submit",
        "data-t": "cta-button",
        caption: label,
        form: formId,
        isLoading: isLoading,
        size: isSmallScreen ? 'custom' : 'medium',
        className: "configurator-footer-cta"
    })
;

const DEFAULT_CTA_LABEL_KEY = 'products.glasses.configurator.footer.CTA';
const CallToActionContainer = ()=>{
    var ref;
    const { getLabel  } = useIntl();
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { configuratorId: id  } = useConfiguratorContext();
    const { lastStepCTALabelKey , selectorProps , stepsMap  } = usePrivateSteppingContext();
    const { currentStep  } = useCurrentStepContext();
    const { labelKeys  } = currentStep;
    const fallbackCtaLabel = {
        key: labelKeys.alternativeCta || DEFAULT_CTA_LABEL_KEY,
        value: undefined,
        overrideLastCta: false
    };
    const isOnLastStep = useSelector((s)=>isLastStep(s, selectorProps)
    );
    const { name  } = useSelector((s)=>getCurrentStep(s, selectorProps)
    ) || {};
    const ctaLabelSelector = name ? (ref = stepsMap[name]) === null || ref === void 0 ? void 0 : ref.ctaLabel : undefined;
    const ctaLabel = useSelector((s)=>ctaLabelSelector ? ctaLabelSelector(s, {
            defaultCtaLabel: DEFAULT_CTA_LABEL_KEY,
            alternativeCtaLabel: labelKeys.alternativeCta
        }) : fallbackCtaLabel
    );
    const finishCall = useSelector((s)=>getFinishCall(s, {
            id
        })
    );
    const labelValue = React.useMemo(()=>{
        if (isOnLastStep && !ctaLabel.overrideLastCta) return getLabel(lastStepCTALabelKey);
        if (ctaLabel.value) return ctaLabel.value;
        if (ctaLabel.key) return getLabel(ctaLabel.key);
        return getLabel(fallbackCtaLabel.key);
    }, [
        isOnLastStep,
        getLabel,
        ctaLabel
    ]);
    return /*#__PURE__*/ React.createElement(CallToAction, {
        label: labelValue,
        isLoading: statusLoading(finishCall || idleState()),
        isSmallScreen: isSmallScreen,
        formId: id
    });
};

const ExpanderToggle = ({ label , isExpanded , onToggleClick  })=>/*#__PURE__*/ React.createElement(IconButton, {
        outline: true,
        onClick: onToggleClick,
        "aria-expanded": isExpanded,
        "aria-label": label,
        "data-t": "expander-toggle",
        className: "configurator-footer-expander-toggle"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-up",
        height: 18,
        width: 18
    }))
;

const ExpanderToggleContainer = (props)=>{
    const dispatch = useDispatch();
    const { getLabel  } = useIntl();
    const { isFooterOpen , configuratorId: id  } = useConfiguratorContext();
    const { footerExpanderContent  } = usePrivateSteppingContext();
    const toggleFooter$1 = React.useCallback(()=>{
        dispatch(toggleFooter({
            id
        }));
    }, [
        dispatch,
        id
    ]);
    const label = React.useMemo(()=>getLabel('products.glasses.configurator.footer.toggle')
    , [
        getLabel, 
    ]);
    // Hide the expander toggle if no expander content was received
    return !footerExpanderContent ? null : /*#__PURE__*/ React.createElement(ExpanderToggle, swcHelpers.extends({}, props, {
        label: label,
        isExpanded: isFooterOpen,
        onToggleClick: toggleFooter$1
    }));
};

const TopBar = ({ children  })=>/*#__PURE__*/ React.createElement("div", {
        className: "configurator-footer-topbar"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "configurator-footer-topbar__content"
    }, children), /*#__PURE__*/ React.createElement("div", {
        className: "configurator-footer-topbar__controls"
    }, /*#__PURE__*/ React.createElement(ExpanderToggleContainer, null), /*#__PURE__*/ React.createElement(CallToActionContainer, null)))
;

const ConfiguratorFooter = ()=>{
    const { footerTopBarContent , footerExpanderContent  } = usePrivateSteppingContext();
    return /*#__PURE__*/ React.createElement("footer", {
        "data-t": "configurator-footer",
        className: "configurator-footer"
    }, /*#__PURE__*/ React.createElement(TopBar, null, footerTopBarContent), /*#__PURE__*/ React.createElement(ExpanderContainer, null, footerExpanderContent));
};

var ConfiguratorHeader = (_param)=>{
    var { className , currentStepIndex , disableSteps , skipSteps , stepNameLabels  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "currentStepIndex",
        "disableSteps",
        "skipSteps",
        "stepNameLabels"
    ]);
    const isPhone = MediaQuery.useIsPhone();
    return /*#__PURE__*/ React.createElement("div", {
        "data-t": rest['data-t'],
        className: classnames('configurator-header', className)
    }, /*#__PURE__*/ React.createElement(WizardProgress, {
        steps: stepNameLabels,
        active: currentStepIndex,
        disableSteps: disableSteps,
        skipSteps: skipSteps,
        labelStyle: isPhone ? 'hidden' : 'inline'
    }));
};

const filterUniqueElements = (value, index, self)=>self.indexOf(value) === index
;
const getStepNames = createSelector(getStepsList, (steps)=>steps.map((s)=>s.name
    )
);
const getSubSteps = createSelector(getStepsList, (steps)=>steps.reduce((acc, step, i)=>step.isSubStep ? [
            ...acc,
            i
        ] : acc
    , [])
);
// the number of subSteps before each index, should be deducted from it
const getSubStepsSubtractor = createSelector(getSubSteps, (subSteps)=>(stepIndex)=>stepIndex - subSteps.filter((sub)=>sub < stepIndex
        ).length
);
// We disable all steps from the active step onwards, because we don't support jumping forward by clicking in the header
const getDisableSteps = createSelector(getStepNames, getCurrentStepIndex, getSubStepsSubtractor, (stepNames, currentStepIndex, subStepSubtractor)=>isDefined(currentStepIndex) ? stepNames.map((_, i)=>i
    ).slice(currentStepIndex + 1).map(subStepSubtractor).filter(filterUniqueElements) : []
);
const getSkipSteps = createSelector(getStepsList, getSubStepsSubtractor, (steps, subStepSubtractor)=>steps.reduce((acc, step, i)=>step.skip ? [
            ...acc,
            i
        ] : acc
    , []).map(subStepSubtractor).filter(filterUniqueElements)
);
const getActiveStepIndex = createSelector(getCurrentStepIndex, getSubSteps, getSubStepsSubtractor, (currentStepIndex, subSteps, subStepSubtractor)=>{
    if (!isDefined(currentStepIndex)) {
        return currentStepIndex;
    }
    // if current step is a subStep, we will return it's parent Index
    if (subSteps.includes(currentStepIndex)) {
        let parentIndex = currentStepIndex - 1;
        while(subSteps.includes(parentIndex) && parentIndex >= 0){
            parentIndex -= 1;
        }
        return subStepSubtractor(parentIndex);
    }
    // otherwise, like other indices we will deduct the number of subSteps behind it
    return subStepSubtractor(currentStepIndex);
});
const getEnabledStepLabels = createSelector(stepsMapProp, getStepsList, getSubSteps, (stepsMap, steps, subSteps)=>{
    return steps// removing subStep from the labels
    .filter((_, index)=>!subSteps.includes(index)
    ).reduce((m, { name  })=>{
        const stepData = stepsMap[name];
        return (stepData === null || stepData === void 0 ? void 0 : stepData.labelKeys.stepIndicatorTitle) ? [
            ...m,
            stepData.labelKeys.stepIndicatorTitle
        ] : m;
    }, []);
});
const getStepWizardData = createSelector(getActiveStepIndex, getEnabledStepLabels, getSkipSteps, getDisableSteps, (currentStepIndex, enabledStepLabels, skipSteps, disableSteps)=>({
        skipSteps,
        disableSteps,
        currentStepIndex,
        enabledStepLabels
    })
);

const ConfiguratorHeaderContainer = ()=>{
    const { getLabel  } = useIntl();
    const { selectorProps  } = usePrivateSteppingContext();
    const { enabledStepLabels , currentStepIndex , disableSteps , skipSteps ,  } = useSelector((s)=>getStepWizardData(s, selectorProps)
    );
    if (!isDefined(currentStepIndex)) {
        throw new Error(`Need current step index to render configurator header. Received: "${currentStepIndex}".`);
    }
    return /*#__PURE__*/ React__default.createElement(ConfiguratorHeader, {
        stepNameLabels: enabledStepLabels.map((l)=>getLabel(l)
        ),
        currentStepIndex: currentStepIndex,
        disableSteps: disableSteps,
        skipSteps: skipSteps
    });
};

var ConfiguratorMain = (_param)=>{
    var { className , currentStepName , children  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "currentStepName",
        "children"
    ]);
    const mainRef = /*#__PURE__*/ React.createRef();
    const isPhone = MediaQuery.useIsPhone();
    React.useLayoutEffect(()=>{
        var ref;
        if (!isPhone) {
            return;
        }
        mainRef === null || mainRef === void 0 ? void 0 : (ref = mainRef.current) === null || ref === void 0 ? void 0 : ref.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [
        currentStepName
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        ref: mainRef,
        "data-t": rest['data-t'],
        className: className
    }, children);
};

const ConfiguratorMainContainer = (props)=>{
    const { currentStepName  } = useCurrentStepContext();
    return /*#__PURE__*/ React__default.createElement(ConfiguratorMain, swcHelpers.extends({
        currentStepName: currentStepName
    }, props));
};

const Configurator = ({ 'data-t': dataT , className , isOpen , preventVerticalScroll , onModalClose ,  })=>/*#__PURE__*/ React__default.createElement(ConfiguratorModal, {
        isOpen: isOpen,
        onModalClose: onModalClose,
        preventVerticalScroll: preventVerticalScroll
    }, /*#__PURE__*/ React__default.createElement("div", {
        "data-t": dataT,
        className: classnames('configurator', className)
    }, /*#__PURE__*/ React__default.createElement(CurrentStepContextProvider, null, /*#__PURE__*/ React__default.createElement(ConfiguratorHeaderContainer, null), /*#__PURE__*/ React__default.createElement(ConfiguratorMainContainer, {
        className: "configurator__main"
    }, /*#__PURE__*/ React__default.createElement(ConfiguratorStepContainer, null)), /*#__PURE__*/ React__default.createElement(ConfiguratorFooter, null))))
;

const useStepHandlers = (id)=>{
    const dispatch = useDispatch();
    const goToNextStep = React.useCallback((e)=>{
        e === null || e === void 0 ? void 0 : e.preventDefault();
        dispatch(nextStep({
            id
        }));
    }, [
        dispatch,
        id
    ]);
    const goToPreviousStep = React.useCallback((e)=>{
        e === null || e === void 0 ? void 0 : e.preventDefault();
        dispatch(previousStep({
            id
        }));
    }, [
        dispatch,
        id
    ]);
    return {
        goToPreviousStep,
        goToNextStep
    };
};
const ConfiguratorContainer = (_param)=>{
    var { id , stepsMap , stepSequence , onFinish , lastStepCTALabelKey , footerExpanderContent , footerTopBarContent  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "id",
        "stepsMap",
        "stepSequence",
        "onFinish",
        "lastStepCTALabelKey",
        "footerExpanderContent",
        "footerTopBarContent"
    ]);
    var ref;
    const dispatch = useDispatch();
    // For the header
    const steps = useSelector((s)=>getStepsList(s, {
            id,
            stepsMap,
            stepSequence
        })
    );
    const currentStepName = (ref = steps.find((s)=>s.isCurrent
    )) === null || ref === void 0 ? void 0 : ref.name;
    const isFooterOpen$1 = useSelector((s)=>isFooterOpen(s, {
            id
        })
    );
    const hasAttemptedNextStep$1 = useSelector((s)=>hasAttemptedNextStep(s, {
            id
        })
    );
    const { rootSaga  } = React.useMemo(()=>configuratorFactory(id)
    , [
        id
    ]);
    const task = useSaga(rootSaga, {
        onStart (t) {
            t.setContext({
                [id]: {
                    stepsMap,
                    stepSequence,
                    onFinish
                }
            });
            dispatch(startConfigurator({
                id,
                stepSequence,
                stepsMap
            }));
        }
    }, [
        dispatch
    ]);
    React.useEffect(()=>{
        task === null || task === void 0 ? void 0 : task.setContext({
            [id]: {
                stepsMap,
                stepSequence,
                onFinish
            }
        });
    }, [
        task,
        stepsMap,
        stepSequence,
        onFinish
    ]);
    const { goToNextStep , goToPreviousStep  } = useStepHandlers(id);
    if (!isDefined(currentStepName)) {
        return null;
    }
    const contextValues = {
        configuratorId: id,
        hasAttemptedNextStep: hasAttemptedNextStep$1,
        isFooterOpen: isFooterOpen$1,
        goToNextStep,
        goToPreviousStep,
        onFinish
    };
    return /*#__PURE__*/ React.createElement(PrivateSteppingContextProvider, {
        configuratorId: id,
        stepsMap: stepsMap,
        stepSequence: stepSequence,
        lastStepCTALabelKey: lastStepCTALabelKey,
        footerTopBarContent: footerTopBarContent,
        footerExpanderContent: footerExpanderContent
    }, /*#__PURE__*/ React.createElement(ConfiguratorContextProvider, swcHelpers.extends({}, contextValues), /*#__PURE__*/ React.createElement(Configurator, swcHelpers.extends({}, rest))));
};

var StepForm = (_param)=> {
    var { configuratorId , onSubmit , children , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "configuratorId",
        "onSubmit",
        "children",
        "className"
    ]);
    return React__default.createElement("form", {
        noValidate: true,
        onSubmit: onSubmit,
        id: configuratorId,
        "aria-label": rest['aria-label'],
        "data-t": rest['data-t'],
        className: classnames('step-form', className)
    }, children);
};
var ConnectedStepForm = (_param)=>{
    var { children , initialFormState , onChange , configuratorId , onSubmit , className  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "initialFormState",
        "onChange",
        "configuratorId",
        "onSubmit",
        "className"
    ]);
    const handleSubmit = React__default.useCallback(()=>onSubmit()
    , [
        onSubmit
    ]);
    return /*#__PURE__*/ React__default.createElement(Form, {
        name: configuratorId,
        "aria-label": rest['aria-label'],
        "data-t": rest['data-t'],
        className: classnames('step-form', className),
        initialFormState: initialFormState,
        onChange: onChange,
        onSubmit: handleSubmit
    }, children);
};

const StepFormContainer = (props)=>{
    const { getLabel  } = useIntl();
    const { configuratorId  } = useConfiguratorContext();
    const { currentStep  } = useCurrentStepContext();
    const { labelKeys  } = currentStep;
    const labels = React__default.useMemo(()=>({
            ariaLabel: getLabel(labelKeys.title)
        })
    , [
        getLabel,
        labelKeys.title
    ]);
    return /*#__PURE__*/ React__default.createElement(StepForm, swcHelpers.extends({}, props, {
        configuratorId: configuratorId,
        "aria-label": labels.ariaLabel
    }));
};
const ConnectedStepFormContainer = (props)=>{
    const { getLabel  } = useIntl();
    const { configuratorId  } = useConfiguratorContext();
    const { currentStep  } = useCurrentStepContext();
    const { labelKeys  } = currentStep;
    const labels = React__default.useMemo(()=>({
            ariaLabel: getLabel(labelKeys.title)
        })
    , [
        getLabel,
        labelKeys.title
    ]);
    return /*#__PURE__*/ React__default.createElement(ConnectedStepForm, swcHelpers.extends({}, props, {
        configuratorId: configuratorId,
        "aria-label": labels.ariaLabel
    }));
};

ReducersRegistry.register('configurators', reducer);

export { actions as Actions, CallToAction, ConfiguratorContainer as Configurator, ConfiguratorHeader, ConfiguratorStep, ConfiguratorStepTitleAndDescriptionContainer as ConfiguratorStepTitleAndDescription, ConnectedStepFormContainer as ConnectedStepForm, Expander, ExpanderToggle, selectors as Selectors, StepFormContainer as StepForm, ctaLabelSelectorProps, isDynamicStep, useConfiguratorContext };
//# sourceMappingURL=index.js.map
